import React, { useState } from 'react'
import { Button } from '@carbon/react'
import { Send } from '@carbon/icons-react'
import {
  FormInput,
  FormTextarea,
  FormSelect,
} from '@sphere/shared/ui/design-system'
import { useToast } from '@sphere/shared/util'
import {
  defaultRecentEmailsQuery,
  useEmails,
  useSendEmail,
} from '@sphere/communication/data-access'
import { CreateEmailDto, EmailPriority } from '@sphere/communication/domain'
import {
  EmailRecentSendsList,
  mapEmailToRecentSendRow,
} from './EmailRecentSendsList'
import styles from './EmailView.module.scss'

const EMAIL_PRIORITY_OPTIONS = [
  { value: EmailPriority.LOW, label: 'Basse' },
  { value: EmailPriority.NORMAL, label: 'Normale' },
  { value: EmailPriority.HIGH, label: 'Haute' },
]

export const EmailView: React.FC = () => {
  const { toast } = useToast()
  const sendEmailMutation = useSendEmail()
  const {
    data: recentEmails,
    isLoading: recentLoading,
    isError: recentError,
  } = useEmails(defaultRecentEmailsQuery)

  const [formData, setFormData] = useState({
    recipientEmail: '',
    recipientName: '',
    subject: '',
    message: '',
    priority: EmailPriority.NORMAL,
  })

  const messageTooShort =
    formData.message.length > 0 && formData.message.length < 10

  const recentRows =
    recentEmails?.data.map(mapEmailToRecentSendRow) ?? []

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const emailPayload: CreateEmailDto = {
      subject: formData.subject,
      body: formData.message,
      priority: formData.priority,
      recipients: [
        {
          email: formData.recipientEmail,
          name: formData.recipientName || undefined,
        },
      ],
    }

    try {
      await sendEmailMutation.mutateAsync(emailPayload)

      toast({
        title: 'Email envoyé',
        description: `Votre message a été envoyé à ${formData.recipientEmail}`,
      })

      setFormData({
        recipientEmail: '',
        recipientName: '',
        subject: '',
        message: '',
        priority: EmailPriority.NORMAL,
      })
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string | string[] } }
      }
      const errorMessage = err?.response?.data?.message
      const description = Array.isArray(errorMessage)
        ? errorMessage.join(', ')
        : errorMessage || "Impossible d'envoyer l'email. Veuillez réessayer."

      toast({
        variant: 'destructive',
        title: 'Erreur',
        description,
      })
    }
  }

  const sending = sendEmailMutation.isPending

  return (
    <div className={styles.page}>
      <div className={styles.mainColumn}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Nouveau message</h3>
            <p className={styles.sectionDescription}>
              Envoyez un email direct à un élève, un parent ou un collègue.
            </p>
          </div>
          <form onSubmit={handleSubmit}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <FormInput
                  id="email-recipient"
                  name="recipientEmail"
                  type="email"
                  label="Email destinataire"
                  placeholder="exemple@ecole.fr"
                  required
                  value={formData.recipientEmail}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      recipientEmail: e.target.value,
                    })
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <FormInput
                  id="email-recipient-name"
                  name="recipientName"
                  label="Nom (optionnel)"
                  placeholder="Jean Dupont"
                  value={formData.recipientName}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      recipientName: e.target.value,
                    })
                  }
                />
              </div>

              <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                <FormInput
                  id="email-subject"
                  name="subject"
                  label="Sujet (minimum 3 caractères)"
                  placeholder="Sujet de votre message"
                  required
                  minLength={3}
                  value={formData.subject}
                  onChange={e =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <FormSelect
                  id="email-priority"
                  name="priority"
                  label="Priorité"
                  required
                  value={formData.priority}
                  onValueChange={value =>
                    setFormData({
                      ...formData,
                      priority: value as EmailPriority,
                    })
                  }
                  options={EMAIL_PRIORITY_OPTIONS}
                  placeholder="Sélectionner..."
                />
              </div>

              <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                <FormTextarea
                  id="email-message"
                  name="message"
                  label="Message (minimum 10 caractères)"
                  placeholder="Votre message..."
                  required
                  minLength={5}
                  value={formData.message}
                  onChange={e =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  error={
                    messageTooShort
                      ? `Le message doit contenir au moins 10 caractères (${formData.message.length}/10)`
                      : undefined
                  }
                />
              </div>
            </div>

            <div className={styles.submitRow}>
              <Button type="submit" disabled={sending} renderIcon={Send}>
                {sending ? 'Envoi en cours...' : 'Envoyer le message'}
              </Button>
            </div>
          </form>
        </section>
      </div>

      <aside className={styles.sidebar}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Listes de diffusion</h3>
            <p className={styles.sectionDescription}>
              Fonctionnalité à venir : créez des groupes de contacts pour vos
              envois fréquents.
            </p>
          </div>
          <p className={styles.placeholder}>
            Pour l'instant, veuillez saisir les adresses emails manuellement.
            L'import de contacts et la création de groupes seront disponibles
            prochainement.
          </p>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Derniers envois</h3>
          </div>
          <EmailRecentSendsList
            rows={recentRows}
            isLoading={recentLoading}
            isError={recentError}
          />
        </section>
      </aside>
    </div>
  )
}
